import { useCallback, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { boatMediaApi, type ClassifyPendingResult } from '../services/boatMediaApi';
import { documentKeys } from '../components/documents/hooks/useDocuments';
import { boatKeys } from './useBoats';
import { useCan } from '@/modules/tenant/hooks/useTenantAuth';
import { AI_PERMISSION_DENIED_STATUS } from './paidAiGuard';

export const boatMediaKeys = {
  all: ['boat-media'] as const,
  details: () => [...boatMediaKeys.all, 'detail'] as const,
  detail: (boatId: string) => [...boatMediaKeys.details(), boatId] as const,
};

export type UploadStatus = 'pending' | 'uploading' | 'success' | 'error';

export interface PendingUpload {
  id: string;
  file: File;
  status: UploadStatus;
  progress: number;
  error?: string;
}

const MAX_CONCURRENT_UPLOADS = 4;
const SUCCESS_TILE_LINGER_MS = 600;

export function useBoatMedia(boatId: string) {
  return useQuery({
    queryKey: boatMediaKeys.detail(boatId),
    queryFn: () => boatMediaApi.getMedia(boatId),
    enabled: !!boatId,
  });
}

export function useUploadBoatMedia() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ boatId, type, files }: { boatId: string; type: 'image' | 'video'; files: File[] }) =>
      boatMediaApi.uploadMedia(boatId, type, files),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: boatMediaKeys.detail(variables.boatId) });
      queryClient.invalidateQueries({ queryKey: boatKeys.detail(variables.boatId) });
    },
  });
}

/**
 * Drop-zone-grade upload orchestrator: per-file requests with progress
 * reporting and a concurrency cap, so the UI can render an optimistic
 * pending tile per dropped file and fill its progress bar in real time.
 *
 * Why not useMutation: TanStack mutations don't expose per-call progress
 * across N parallel calls cleanly, and we need to interleave queue/in-
 * flight/error state across an unbounded set of files. A small inline
 * promise pool over a ref-backed queue is simpler than a mutation
 * orchestrator and avoids re-renders on every progress event.
 */
export function useBoatMediaUploads(boatId: string) {
  const queryClient = useQueryClient();
  const [uploads, setUploads] = useState<PendingUpload[]>([]);
  const counterRef = useRef(0);
  const queueRef = useRef<PendingUpload[]>([]);
  const inFlightRef = useRef(0);

  const updateUpload = useCallback((id: string, patch: Partial<PendingUpload>) => {
    setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, ...patch } : u)));
  }, []);

  const removeUpload = useCallback((id: string) => {
    setUploads((prev) => prev.filter((u) => u.id !== id));
  }, []);

  const runOne = useCallback(
    async (upload: PendingUpload) => {
      updateUpload(upload.id, { status: 'uploading', progress: 0, error: undefined });
      try {
        await boatMediaApi.uploadMediaFile(
          boatId,
          'image',
          upload.file,
          (event) => {
            const total = event.total ?? upload.file.size;
            const pct = total
              ? Math.min(100, Math.round((event.loaded / total) * 100))
              : 0;
            updateUpload(upload.id, { progress: pct });
          },
        );
        updateUpload(upload.id, { status: 'success', progress: 100 });
        // Refetch so the real server tile appears, then drop the pending
        // tile after a short linger so the swap doesn't flicker.
        queryClient.invalidateQueries({ queryKey: boatMediaKeys.detail(boatId) });
        queryClient.invalidateQueries({ queryKey: boatKeys.detail(boatId) });
        setTimeout(() => removeUpload(upload.id), SUCCESS_TILE_LINGER_MS);
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Upload failed';
        updateUpload(upload.id, { status: 'error', error: message });
      }
    },
    [boatId, queryClient, removeUpload, updateUpload],
  );

  const drain = useCallback(() => {
    while (
      inFlightRef.current < MAX_CONCURRENT_UPLOADS &&
      queueRef.current.length > 0
    ) {
      const upload = queueRef.current.shift()!;
      inFlightRef.current += 1;
      void runOne(upload).finally(() => {
        inFlightRef.current -= 1;
        drain();
      });
    }
  }, [runOne]);

  const enqueue = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      const newUploads: PendingUpload[] = files.map((file) => ({
        id: `pending-${++counterRef.current}`,
        file,
        status: 'pending',
        progress: 0,
      }));
      setUploads((prev) => [...prev, ...newUploads]);
      queueRef.current.push(...newUploads);
      drain();
    },
    [drain],
  );

  const retry = useCallback(
    (id: string) => {
      let target: PendingUpload | undefined;
      setUploads((prev) =>
        prev.map((u) => {
          if (u.id !== id || u.status !== 'error') return u;
          target = { ...u, status: 'pending', progress: 0, error: undefined };
          return target;
        }),
      );
      if (target) {
        queueRef.current.push(target);
        drain();
      }
    },
    [drain],
  );

  const dismiss = useCallback((id: string) => removeUpload(id), [removeUpload]);

  const inFlightCount = uploads.filter(
    (u) => u.status === 'pending' || u.status === 'uploading',
  ).length;

  return {
    uploads,
    enqueue,
    retry,
    dismiss,
    isUploading: inFlightCount > 0,
    inFlightCount,
  };
}

export function useDeleteBoatMedia() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ boatId, mediaId }: { boatId: string; mediaId: number }) =>
      boatMediaApi.deleteMedia(boatId, mediaId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: boatMediaKeys.detail(variables.boatId) });
      queryClient.invalidateQueries({ queryKey: boatKeys.detail(variables.boatId) });
    },
  });
}

export function useReorderBoatMedia() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ boatId, mediaIds }: { boatId: string; mediaIds: number[] }) =>
      boatMediaApi.reorderMedia(boatId, mediaIds),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: boatMediaKeys.detail(variables.boatId) });
      queryClient.invalidateQueries({ queryKey: boatKeys.all });
    },
  });
}

export function useSetPrimaryBoatImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ boatId, mediaId }: { boatId: string; mediaId: number }) =>
      boatMediaApi.setPrimaryImage(boatId, mediaId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: boatMediaKeys.detail(variables.boatId) });
      queryClient.invalidateQueries({ queryKey: boatKeys.detail(variables.boatId) });
    },
  });
}

/** #1219 — outcome of "Use as photo". */
export type ConfirmPendingOutcome = 'confirmed' | 'already_sorted';

/**
 * #1219 — "Use as photo": move a held image into the gallery. The server
 * answers 404 for any media that is not held — including one the classifier
 * promoted a moment ago — so a 404 is "already sorted", not an error. Either
 * way the gallery is refetched.
 */
export function useConfirmPendingPhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ boatId, mediaId }: { boatId: string; mediaId: number }): Promise<ConfirmPendingOutcome> => {
      try {
        await boatMediaApi.confirmPendingPhoto(boatId, mediaId);
        return 'confirmed';
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 404) {
          return 'already_sorted';
        }
        throw error;
      }
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: boatMediaKeys.detail(variables.boatId) });
      queryClient.invalidateQueries({ queryKey: boatKeys.detail(variables.boatId) });
    },
  });
}

/**
 * #1219 — "Sort again": re-run the boat's intake batch. A run can move media
 * between the gallery, the documents and the held set, so all three refresh.
 */
export function useClassifyPendingImages() {
  const queryClient = useQueryClient();
  // ACL-09 (#611, D-22) — sorting re-runs the paid classifier. Without
  // inventory.ai.generate it resolves locally to skipped_no_permission (#1458
  // WR-04: its own outcome, not the server's skipped_ai_disabled), so nothing
  // is sent and nothing needs refreshing.
  const canGenerate = useCan('inventory.ai.generate');

  return useMutation({
    mutationFn: ({ boatId }: { boatId: string }): Promise<ClassifyPendingResult> =>
      canGenerate
        ? boatMediaApi.classifyPendingImages(boatId)
        : Promise.resolve({ status: AI_PERMISSION_DENIED_STATUS, media_count: 0 }),
    onSuccess: (data, variables) => {
      if (data.status === 'skipped_ai_disabled' || data.status === AI_PERMISSION_DENIED_STATUS) return;
      queryClient.invalidateQueries({ queryKey: boatMediaKeys.detail(variables.boatId) });
      queryClient.invalidateQueries({ queryKey: documentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: boatKeys.detail(variables.boatId) });
    },
  });
}
